import { Button, Card, Flex, Modal } from "antd";
import React, { useMemo, useState } from "react";
import { DryerSettingsText } from "./DryerSettingsText";
import { SettingsTable } from "./SettingsTable";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import { formatNumber } from "src/utils/formatting";
import {
  getPremerPlusMachineTypeString,
  getPremierPlusFanTypeString,
  getPremierPlusLgsoModeString,
  getSweepOffTimeLabel,
} from "src/utils/enumUtils";
import {
  convertFahrenheitToCelsius,
  convertFahrenheitToCelsiusOffset,
} from "src/utils/generalUtils";

interface DryerSettingsCardProps {
  binDto: PremierPlusBinDTO | null | undefined;
}

export const DryerSettingsCard: React.FC<DryerSettingsCardProps> = ({ binDto }) => {
  const [showTableSettings, setShowTableSettings] = useState(false);

  const dryingSettings = useMemo(
    () => binDto?.settings?.dryerSettings,
    [binDto]
  );

  const useCelsius = binDto?.platformSettings?.useMetricSystem === true;

  const unit = useMemo(() => (useCelsius ? "°C" : "°F"), [useCelsius]);

  const toDisplayTemp = useMemo(() => {
    return (value?: number | null) => {
      if (value == null) return value;
      return useCelsius ? convertFahrenheitToCelsius(value) : value;
    };
  }, [useCelsius]);

  const toDisplayOffset = useMemo(() => {
    return (value?: number | null) => {
      if (value == null) return value;
      return useCelsius ? convertFahrenheitToCelsiusOffset(value) : value;
    };
  }, [useCelsius]);

  const text = useMemo(() => {
    const lines: string[] = [];

    lines.push(
      `Transfer Moisture=${formatNumber(dryingSettings?.targetMcPercent, {
        decimalPlaces: 1,
        suffix: "%",
        filler: "--",
      })}`
    );

    lines.push(
      `Target Temp=${formatNumber(
        toDisplayTemp(dryingSettings?.targetPlenumTemperatureF),
        {
          decimalPlaces: 1,
          suffix: unit,
          filler: "--",
        }
      )}`
    );

    binDto?.augers?.forEach((a, idx) => {
      lines.push(`Aug ${idx + 1} Delay=${a.cleanoutDelaySeconds}`);
    });

    lines.push(
      `Max Temp=${formatNumber(
        toDisplayTemp(dryingSettings?.maxPlenumTemperatureF),
        {
          decimalPlaces: 1,
          suffix: unit,
          filler: "--",
        }
      )}`
    );

    lines.push(
      `Min Temp=${formatNumber(
        toDisplayTemp(dryingSettings?.minPlenumTemperatureF),
        {
          decimalPlaces: 1,
          suffix: unit,
          filler: "--",
        }
      )}`
    );

    lines.push(
      `Plenum Temp Cal=${formatNumber(
        toDisplayOffset(dryingSettings?.plenumTempCalibrationF),
        {
          decimalPlaces: 1,
          suffix: unit,
          filler: "--",
        }
      )}`
    );

    lines.push(
      `Grain Temp Cal=${formatNumber(
        toDisplayOffset(dryingSettings?.grainTempCalibrationF),
        {
          decimalPlaces: 1,
          suffix: unit,
          filler: "--",
        }
      )}`
    );

    lines.push(
      `Moisture Cal=${formatNumber(dryingSettings?.grainMcCalibrationPercent, {
        decimalPlaces: 1,
        suffix: "%",
        filler: "--",
      })}`
    );

    lines.push(
      `Sweep Off Time=${getSweepOffTimeLabel(dryingSettings?.sweepOffTime)}`
    );

    lines.push(
      `Fan shutdown=${formatNumber(dryingSettings?.fanShutOffDelayMinutes, {
        decimalPlaces: 1,
        suffix: "sec",
        filler: "--",
      })}`
    );

    lines.push(`Fan type=${getPremierPlusFanTypeString(binDto?.fanTypeId)}`);
    lines.push(
      `Check Moist Change=${
        dryingSettings?.disableMoistureChangingCheck === true ? "No" : "Yes"
      }`
    );
    lines.push(
      `Low grain shutdown=${getPremierPlusLgsoModeString(dryingSettings?.lgsoMode)}`
    );
    lines.push(`Bin Name=${binDto?.binName}`);
    lines.push(`Grain Type=${dryingSettings?.grainType}`);
    lines.push(`Number of samples in avg=${dryingSettings?.samplesCntInAverage}`);
    lines.push(`Machine type=${getPremerPlusMachineTypeString(binDto?.machineType)}`);
    lines.push(
      `Version=${binDto?.platformState?.platformVersion}-${binDto?.platformState?.hmiVersion}`
    );

    return lines.join("\r\n");
  }, [binDto, dryingSettings, toDisplayTemp, toDisplayOffset, unit]);

  return (
    <>
      <Card
        loading={false}
        title="Dryer Settings"
        style={{ width: "100%" }}
        styles={{
          body: {
            padding: 0,
            minWidth: "296px",
            width: "100%",
            minHeight: "504px",
          },
        }}
        extra={<Button onClick={() => setShowTableSettings(true)}>History</Button>}
      >
        <div style={{ width: "100%" }}>
          <Flex justify="center" style={{ width: "100%" }}>
            <DryerSettingsText text={text} />
          </Flex>
        </div>
      </Card>

      <Modal
        title="Dryer Settings History"
        styles={{ body: { overflowY: "auto", maxHeight: "calc(100vh - 200px)" } }}
        width="70%"
        footer={null}
        open={showTableSettings}
        onOk={() => setShowTableSettings(false)}
        onCancel={() => setShowTableSettings(false)}
      >
        <SettingsTable binId={binDto?.binId} />
      </Modal>
    </>
  );
};